/**
 * What a template looks like, rendered from the template itself.
 *
 * A folder of PNGs would have been less code and it would have been wrong: a screenshot goes
 * stale the first time a shape, a colour or a layout changes, and it goes stale silently — the
 * card keeps promising a map the product no longer makes. Rendering the template's own document
 * through the same scene model the canvas draws and the same exporter the SVG download uses means
 * a preview cannot disagree with what opening it produces.
 *
 * The layout runs here rather than being baked into the template, and that is the same decision
 * the editor makes on open: node boxes are sized by measuring their text, and text measures
 * differently in different fonts on different machines. Coordinates saved against one measurer
 * and drawn against another are boxes that overlap. Arranging with the measurer that is about to
 * draw is the only version of this that is right everywhere.
 *
 * It is a *view*, not an edit: no command, no journal, no engine. The document handed back to the
 * layout is a throwaway copy, and the real map arranges itself on first open like any other.
 */
import type { MapDocument } from '@orbit-mind/domain';
import { sceneToSvg } from '@orbit-mind/export-engine';
import { layoutFor } from '@orbit-mind/layout-engine';
import { buildScene, type Scene } from '@orbit-mind/map-engine';
import type { NodeId, Size } from '@orbit-mind/shared-types';

import { canvasTextStyle, textMeasurer } from '../canvas/measurer';

/** Tighter than an export's: a thumbnail spends its pixels on the map, not on the margin. */
const PREVIEW_PADDING = 16;

function arranged(document: MapDocument, scene: Scene): MapDocument {
  const extents = new Map<NodeId, Size>(
    scene.nodes.map((node) => [node.id, { width: node.rect.width, height: node.rect.height }]),
  );

  const result = layoutFor(document.map.layoutId).run({
    document,
    extentOf: (nodeId) => extents.get(nodeId) ?? { width: 40, height: 32 },
    // A template has no hand-placed nodes — every position in it is the origin, waiting for a
    // layout. Preserving them would preserve a pile of boxes on top of each other.
    preserveManualPositions: false,
  });

  const nodes = Object.fromEntries(
    Object.entries(document.nodes).map(([id, node]) => {
      const placement = result.positions[id as NodeId];
      if (placement === undefined) return [id, node];
      return [
        id,
        {
          ...node,
          x: placement.x,
          y: placement.y,
          ...(placement.width === undefined ? {} : { width: placement.width }),
          ...(placement.height === undefined ? {} : { height: placement.height }),
        },
      ];
    }),
  );

  return { ...document, nodes };
}

/**
 * A standalone SVG of the template, ready for an `<img>`.
 *
 * No background, so the card's own surface shows through and the preview belongs to whichever
 * theme the person is in rather than punching a white rectangle into a dark one.
 */
export function templatePreviewSvg(document: MapDocument, fontFamily: string): string {
  const measurer = textMeasurer();
  const baseTextStyle = canvasTextStyle(fontFamily);

  // Twice, and both are needed: the first scene is only there to measure with, because a layout
  // is given extents and never measures. The second is the one that gets drawn.
  const measured = buildScene(document, { measurer, baseTextStyle });
  const scene = buildScene(arranged(document, measured), { measurer, baseTextStyle });

  return sceneToSvg(scene, {
    padding: PREVIEW_PADDING,
    // The template's own page when it has one (a dark board, a pink sheet); otherwise the card's.
    background: scene.canvas?.background ?? null,
    title: document.map.title,
  });
}

/** The same SVG as something an `<img>` can load without a network or a blob URL to revoke. */
export function templatePreviewSrc(document: MapDocument, fontFamily: string): string {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(templatePreviewSvg(document, fontFamily))}`;
}
